import { useState, useEffect } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const NAV_LINKS = [
  { label: "Accueil", href: "#accueil", testId: "nav-home-link" },
  { label: "À propos", href: "#a-propos", testId: "nav-about-link" },
  { label: "Secteurs", href: "#secteurs", testId: "nav-services-link" },
  { label: "Réalisations", href: "#realisations", testId: "nav-portfolio-link" },
  { label: "Contact", href: "#contact", testId: "nav-contact-link" },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/x4oygmg8_LOGO.webp";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="#accueil"
          onClick={(e) => { e.preventDefault(); handleNavClick("#accueil"); }}
          data-testid="nav-logo"
          className="flex-shrink-0"
        >
          <img
            src={LOGO_URL}
            alt="Toitures TPL inc"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="text-sm uppercase tracking-[0.15em] font-semibold text-[#0F172A] hover:text-[#0077cc] transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <a
          href="tel:5147176662"
          data-testid="nav-phone-cta"
          className="hidden lg:flex items-center gap-2 bg-[#e62e37] text-white font-bold uppercase tracking-wide px-6 py-3 text-sm hover:bg-[#cc222b] transition-colors duration-300"
        >
          <Phone className="w-4 h-4" />
          514-717-6662
        </a>

        {/* Mobile menu */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" className="p-2 text-[#0F172A]">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-[300px]" aria-describedby="mobile-nav-desc">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <p id="mobile-nav-desc" className="sr-only">Liens de navigation du site TPL</p>
              <div className="flex flex-col gap-6 mt-12">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    data-testid={`mobile-${link.testId}`}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="text-lg uppercase tracking-[0.15em] font-semibold text-[#0F172A] hover:text-[#0077cc] transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="tel:5147176662"
                  data-testid="mobile-phone-cta"
                  className="flex items-center gap-2 bg-[#e62e37] text-white font-bold uppercase tracking-wide px-6 py-4 text-sm hover:bg-[#cc222b] transition-colors mt-4 justify-center"
                >
                  <Phone className="w-4 h-4" />
                  514-717-6662
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
